/**
 * The platform's own switches, as the public `GET /auth/platform` reports them
 * (`backend/src/modules/admin/platformSettings.ts`).
 *
 * **A failed read is the defaults, never a refusal.** This is read by the sign-in
 * page and the shell before anybody has a session, often from a field with one
 * bar of signal. A read that could not complete must not be what hides the
 * sign-up form or puts a warning over the app — the API still refuses a closed
 * registration itself, and that refusal is handled where it lands.
 */
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Card } from '@/ui';
import { useT } from '@/i18n';
import './platform.css';

export interface PlatformSettings {
  supportEmail: string | null;
  maintenanceBanner: string | null;
  registrationOpen: boolean;
}

/** The API's code for "no new accounts right now". Sign-in is unaffected. */
export const REGISTRATION_CLOSED = 'registration_closed';

export function usePlatformSettings(): PlatformSettings {
  const { data } = useQuery({
    queryKey: ['auth', 'platform'],
    queryFn: () => api.get<Partial<PlatformSettings>>('/auth/platform'),
    // An admin flips these rarely; every screen asking afresh is noise.
    staleTime: 5 * 60_000,
    retry: 1,
  });
  // Field by field, so a partial or odd answer still lands on a safe default.
  return {
    supportEmail: typeof data?.supportEmail === 'string' && data.supportEmail ? data.supportEmail : null,
    maintenanceBanner:
      typeof data?.maintenanceBanner === 'string' && data.maintenanceBanner.trim() ? data.maintenanceBanner : null,
    // Only an explicit `false` closes it — see the note at the top.
    registrationOpen: data?.registrationOpen !== false,
  };
}

/**
 * The API's code for an account, or an email, phone or provider account,
 * that a platform admin has banned (the user, 2026-10-07, decision 7). Every
 * sign-in door and registration answers it the same way.
 */
export const BANNED = 'banned';

export function isBanned(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === BANNED;
}

/** One sentence for it, wherever it lands — never "incorrect password". */
export function bannedMessage(tr: ReturnType<typeof useT>): string {
  return tr('This account has been banned from MyRave, so it cannot sign in or register again.');
}

/** A sign-up refused because registration closed after the page loaded. */
export function isRegistrationClosed(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === REGISTRATION_CLOSED;
}

/**
 * The sentence every account-creating door shows instead of its form.
 *
 * `role="status"`, not an alert: nothing the person did was wrong, and the
 * sign-in beside it still works.
 */
export function RegistrationClosed({ supportEmail }: { supportEmail?: string | null }) {
  const tr = useT();
  return (
    <p role="status" className="app-p app-muted">
      {tr('Registration is closed right now, so new accounts cannot be created. If you already have an account, you can still sign in.')}
      {/* Plain text, not a `mailto:` — it is an address to read, and a link
          here would open a mail client on a sign-in screen. */}
      {supportEmail && <> {tr('Questions: {email}', { email: supportEmail })}</>}
    </p>
  );
}

/**
 * The platform's maintenance notice, when an admin has set one.
 *
 * Rendered as a TEXT child, so whatever the setting holds is shown as typed —
 * never parsed as markup.
 */
export function MaintenanceBanner({ style }: { style?: CSSProperties }) {
  const { maintenanceBanner } = usePlatformSettings();
  if (!maintenanceBanner) return null;
  return (
    <div data-print="hide" className="app-banner-wrap" style={style}>
      <Card className="platform-banner">
        <span role="status" className="app-banner-text">{maintenanceBanner}</span>
      </Card>
    </div>
  );
}

